import React, { useState } from 'react'
import { Box, Typography, Grid, Card, CardActionArea, CardContent, Paper, CardMedia } from '@mui/material'
import ProfilePage from './Settings/ProfilePage'
import CompanyPage from './Settings/CompanyPage'
import PreferencesPage from './Settings/PreferencesPage'
import CompanyCulture from './Settings/CompanyCulturePage'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface SettingCardProps {
  title: string
  description: string
  index: number
  handleSelection: (index: number) => void
  image?: string
}

const SettingCard: React.FC<SettingCardProps> = ({ title, description, index, handleSelection, image }) => {
  const handleNavigation = () => {
    handleSelection(index)
  }

  return (
    <Card sx={{ display: 'flex', alignItems: 'center' }}>
      <CardActionArea
        onClick={handleNavigation}
        sx={{ minHeight: 170, display: 'flex', px: 1, justifyContent: 'flex-start' }}>
        <CardMedia
          component="img"
          sx={{ width: 150 }}
          image={image}
          alt="Live from space album cover"
        />
        <CardContent>
          <Typography
            variant="h6"
            component="div">
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary">
            {description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

const SettingsPage: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab')

  const [selectedPageIndex, setSelectedPageIndex] = useState<number | null>(() => {
    switch (tabFromUrl) {
      case 'profile':
        return 0
      case 'company':
        return 1
      case 'culture':
        return 2
      case 'preferences':
        return 3
      case 'integrations':
        return 4
      case 'notifications':
        return 5
      default:
        return null
    }
  })

  const settingOptions = [
    {
      title: t('settings.menu.profile'),
      description: t('settings.menu.profileDescription'),
      component: <ProfilePage />,
      image: 'images/user-profile.jpg',
      urlParam: 'profile'
    },
    {
      title: t('settings.menu.company'),
      description: t('settings.menu.companyDescription'),
      component: <CompanyPage />,
      image: 'images/company-profile.jpg',
      urlParam: 'company'
    },
    {
      title: t('settings.menu.culture'),
      description: t('settings.menu.cultureDescription'),
      component: <CompanyCulture />,
      image: 'images/company-culture.jpg',
      urlParam: 'culture'
    },
    {
      title: t('settings.menu.preferences'),
      description: t('settings.menu.preferencesDescription'),
      component: <PreferencesPage />,
      image: 'images/preferences.jpg',
      urlParam: 'preferences'
    },
    {
      title: t('settings.menu.integrations'),
      description: t('settings.menu.integrationsDescription'),
      component: <>Integrations</>,
      image: 'images/department.jpg',
      urlParam: 'integrations'
    },
    {
      title: t('settings.menu.notifications'),
      description: t('settings.menu.notificationsDescription'),
      component: <>Notifications</>,
      image: 'images/notifications.jpg',
      urlParam: 'notifications'
    }
  ]

  const navigate = useNavigate()

  const handleSelection = (index: number) => {
    setSelectedPageIndex(index)
    navigate(`/settings?tab=${settingOptions[index].urlParam}`)
  }

  const isSettingSelected = selectedPageIndex != null

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isSettingSelected ? 'flex-start' : 'center',
        padding: 4,
        gap: 1,
        position: 'relative'
      }}>
      <Grid
        container
        spacing={2}
        sx={{
          width: isSettingSelected ? '30%' : '70%',
          pr: 2,
          position: 'sticky',
          top: 95,
          height: 'fit-content'
        }}>
        {settingOptions.map((setting, index) => (
          <Grid
            item
            lg={isSettingSelected ? 12 : 6}
            xs={12}
            key={setting.title}>
            <SettingCard
              title={setting.title}
              description={setting.description}
              index={index}
              handleSelection={handleSelection}
              image={setting.image}
            />
          </Grid>
        ))}
      </Grid>
      {isSettingSelected && (
        <Box sx={{ width: '70%' }}>
          <Paper sx={{ padding: 3 }}>{settingOptions[selectedPageIndex].component}</Paper>
        </Box>
      )}
    </Box>
  )
}

export default SettingsPage
